import { Controller } from '@hotwired/stimulus'
import { FileDropOverlay } from '../lib/file_drop_overlay'
import { ImageConverter } from '../lib/image_converter'
import { createSpinner } from '../lib/svg_spinner'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB in bytes

export default class extends Controller {
  static targets = [
    'input',
    'preview',
    'defaultView',
    'fileName',
    'removeFileButton',
  ]

  connect = () => {
    this.fileDropOverlay = new FileDropOverlay({
      parentElement: document.body,
      hiddenClass: 'hidden',
      message: 'Drop image to upload',
      onDrop: (files) => {
        this.inputTarget.files = files
        this.addFile(files[0])
      },
    })
  }

  disconnect = () => {
    if (this.fileDropOverlay) {
      this.fileDropOverlay.destroy()
    }
  }

  browse = () => {
    this.inputTarget.click()
  }

  // Handle file selection via the file input
  handleFileSelect = () => {
    if (this.inputTarget.files && this.inputTarget.files[0]) {
      this.addFile(this.inputTarget.files[0])
    }
  }

  addFile = (file) => {
    // Check if the file type matches the accepted types from the input element
    const acceptedTypes = this.inputTarget.getAttribute('accept').split(',')
    const isAccepted = acceptedTypes.some((type) => {
      // Handle wildcards like "image/*"
      if (type.endsWith('/*')) {
        const category = type.split('/')[0]
        return file.type.startsWith(`${category}/`)
      }
      return type === file.type
    })

    // If the file type is not accepted, show an alert and return early
    if (!isAccepted) {
      const acceptedFormats = acceptedTypes.map((type) => {
        return type.replace('image/', '').toUpperCase()
      }).join(', ')
      alert(`Invalid file format. Please use only ${acceptedFormats} files.`)
      this.inputTarget.value = ''

      return
    }

    if (file.size > MAX_FILE_SIZE) {
      alert(`File too large. Please select an image smaller than 20MB.`)
      this.inputTarget.value = ''

      return
    }

    // Hide the default view and show the preview
    if (this.hasDefaultViewTarget) {
      this.defaultViewTargets.forEach((ele) => {
        ele.classList.add('hidden')
      })
    }
    if (this.hasPreviewTarget) {
      if (file.type.match('image.*')) {
        // Show loading state for each preview
        this.previewTargets.forEach((previewContainer) => {
          const image = previewContainer.querySelector('img')
          // Fade the current image if it exists
          if (image.src) {
            image.style.opacity = '0.3'
            image.style.transition = 'opacity 0.3s ease'
          }
          // Make sure the container has relative positioning
          if (getComputedStyle(previewContainer).position === 'static') {
            previewContainer.style.position = 'relative'
          }
          // Remove any existing spinner
          const existingSpinner = previewContainer.querySelector('.spinner-svg')
          if (existingSpinner) {
            existingSpinner.remove()
          }
          // Add spinner
          const spinner = createSpinner()
          previewContainer.appendChild(spinner)
        })
        // Process the image using the ImageConverter
        this.imageConverter.processImageFile(file)
          .then((processedDataUrl) => {
            // Update all preview targets with the processed image
            this.previewTargets.forEach((previewContainer) => {
              const image = previewContainer.querySelector('img')
              // Set the new image source
              image.src = processedDataUrl
              // Remove spinner and restore opacity when image is loaded
              image.onload = () => {
                image.style.opacity = '1'
                // Remove spinner
                const spinner = previewContainer.querySelector('.spinner-svg')
                if (spinner) {
                  spinner.remove()
                }
              }
            })
            // Extract base64 data
            const base64Data = processedDataUrl.split(',')[1]
            // Store in hidden field if target exists
            if (this.hasProcessedImageTarget) {
              this.processedImageTarget.value = base64Data
            }
          })
          .catch((error) => {
            console.error('Error processing image:', error)
            // Remove spinners on failure
            this.previewTargets.forEach((previewContainer) => {
              const spinner = previewContainer.querySelector('.spinner-svg')
              if (spinner) {
                spinner.remove()
              }
              // Restore image opacity
              const image = previewContainer.querySelector('img')
              if (image) {
                image.style.opacity = '1'
              }
              // Optionally add error indicator
              // You could add code here to show an error state
            })
          })
        this.setUI(file)
      }
    }
  }

  setUI = (file) => {
    this.previewTargets.forEach((ele) => {
      ele.classList.remove('hidden')
    })

    if (this.hasFileNameTarget) {
      this.fileNameTargets.forEach((ele) => {
        ele.textContent = file.name
      })
    }

    if (this.hasDefaultViewTarget) {
      this.defaultViewTargets.forEach((ele) => {
        ele.classList.add('hidden')
      })
    }

    // if (this.hasRemoveFieldTarget) {
    //   this.removeFieldTargets.forEach(ele => {
    //     ele.value = '0'
    //   })
    // }
  }

  removeFile = (event) => {
    event.preventDefault()
    event.stopPropagation()

    this.resetUI()
  }

  resetUI = () => {
    this.inputTarget.value = ''

    if (this.hasPreviewTarget) {
      this.previewTargets.forEach((ele) => {
        ele.classList.add('hidden')
      })
    }

    if (this.hasDefaultViewTarget) {
      this.defaultViewTargets.forEach((ele) => {
        ele.classList.remove('hidden')
      })
    }

    if (this.hasFileNameTarget) {
      this.fileNameTargets.forEach((ele) => {
        ele.textContent = ''
      })
    }

    // if (this.hasRemoveFieldTarget) {
    //   this.removeFieldTargets.forEach(ele => {
    //     ele.value = '1'
    //   })
    // }

    if (this.hasRemoveFileTarget) {
      this.removeFileTargets.forEach((ele) => {
        ele.classList.add('hidden')
      })
    }
  }

  get fileDropOver() {
    return document.getElementById('dropOver')
  }

  get imageConverter() {
    return new ImageConverter({
      width: 300,
      height: 300,
      format: 'jpeg',
      quality: 0.9,
      fitMethod: 'crop',
    })
  }
}
